import { useEffect } from "react";
import { formatDistanceToNowStrict } from "date-fns";
import { trackEvent } from "@/lib/analytics";

export interface UnsavedOutfitLite {
  id: string;
  lookbook_status: "pending" | "generating" | "ready" | "failed" | string | null;
  lookbook_image_url: string | null;
  thumbnail_url: string | null;
  image_url: string | null;
  hero_item_name: string | null;
  title: string | null;
  style_summary: string | null;
  created_at: string;
}

interface UnsavedLookPreviewCardProps {
  outfit: UnsavedOutfitLite;
  unsavedCount: number;
  onTap: () => void;
  onRetry?: (outfitId: string) => void;
}

const INK = "#2C2820";
const DIM = "#9B958E";
const CORAL = "#C84B2F";
const BORDER = "#E8E2D9";
const PULSE_BG = "#F1EFE8";

function hoursUntil(createdAt: string): number {
  const created = new Date(createdAt).getTime();
  const expires = created + 7 * 24 * 60 * 60 * 1000;
  return Math.max(0, (expires - Date.now()) / (60 * 60 * 1000));
}

export default function UnsavedLookPreviewCard({
  outfit,
  unsavedCount,
  onTap,
  onRetry,
}: UnsavedLookPreviewCardProps) {
  const status = outfit.lookbook_status ?? "pending";
  const isReady = status === "ready";
  const isFailed = status === "failed";
  const isLoading = status === "pending" || status === "generating";

  const expiresAt = new Date(
    new Date(outfit.created_at).getTime() + 7 * 24 * 60 * 60 * 1000,
  );
  const hoursLeft = hoursUntil(outfit.created_at);
  const urgent = hoursLeft <= 24;
  const timeLeft = urgent
    ? `${Math.max(1, Math.round(hoursLeft))} hour${Math.round(hoursLeft) === 1 ? "" : "s"}`
    : formatDistanceToNowStrict(expiresAt);

  const itemName =
    outfit.hero_item_name || outfit.title || outfit.style_summary || "Your look";

  useEffect(() => {
    trackEvent("home_unsaved_preview_viewed", {
      outfit_id: outfit.id,
      lookbook_status: status,
      hours_to_expiry: Math.round(hoursLeft),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outfit.id]);

  const thumbSrc = isReady
    ? outfit.lookbook_image_url
    : isFailed
    ? outfit.thumbnail_url || outfit.image_url
    : null;

  const ariaLabel = isReady
    ? `Unsaved look: ${itemName}. Tap to save. Expires in ${timeLeft}.`
    : isFailed
    ? `Unsaved look: ${itemName}. Generation failed, tap retry.`
    : `Unsaved look: ${itemName}. Styling in progress.`;

  return (
    <>
      <style>{`
        @keyframes l2f-unsaved-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.55; }
        }
      `}</style>
      <button
        type="button"
        onClick={onTap}
        aria-label={ariaLabel}
        style={{
          display: "block",
          width: "calc(100% - 32px)",
          margin: "0 16px 12px",
          padding: 12,
          background: "#FFFCF8",
          border: `0.5px solid ${BORDER}`,
          borderRadius: 20,
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: 10,
              overflow: "hidden",
              flexShrink: 0,
              background: PULSE_BG,
              animation: isLoading
                ? "l2f-unsaved-pulse 1.5s ease-in-out infinite"
                : undefined,
            }}
          >
            {thumbSrc ? (
              <img
                src={thumbSrc}
                alt=""
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : null}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              className="l2f-type-section-header"
              style={{
                color: DIM,
              }}
            >
              YOUR UNSAVED LOOK
            </div>
            <div
              style={{
                fontSize: 14,
                fontWeight: 500,
                color: INK,
                marginTop: 2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {itemName}
            </div>
            <div style={{ marginTop: 4 }}>
              {isReady ? (
                <span
                  style={{
                    fontSize: 12,
                    color: CORAL,
                    fontWeight: urgent ? 500 : 400,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  {urgent ? (
                    <i className="ti ti-clock" style={{ fontSize: 12 }} />
                  ) : null}
                  Tap to save · Expires in {timeLeft}
                </span>
              ) : isFailed ? (
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    trackEvent("home_unsaved_retry_tapped", {
                      outfit_id: outfit.id,
                    });
                    onRetry?.(outfit.id);
                  }}
                  className="l2f-type-caption"
                  style={{
                    color: CORAL,
                    textDecoration: "underline",
                    cursor: "pointer",
                  }}
                >
                  Couldn&apos;t generate · Retry
                </span>
              ) : (
                <span className="l2f-type-caption" style={{ color: DIM }}>
                  Styling your look...
                </span>
              )}
            </div>
          </div>
        </div>
        {unsavedCount > 1 ? (
          <div
            className="l2f-type-caption"
            style={{
              color: DIM,
              textAlign: "right",
              marginTop: 6,
            }}
          >
            +{unsavedCount - 1} more
          </div>
        ) : null}
      </button>
    </>
  );
}
